/* eslint-disable */
import _m0 from "protobufjs/minimal";

export const protobufPackage = "pathfinder";

export interface PathfinderVersion {
  /**
   * The major version needs to be bumped whenever artifacts are change in a non-backwards
   * compatible way.
   */
  major: PathfinderVersion_ArtifactMajorVersion;
  /**
   * The minor version should be updated for backwards compatible changes. This is simply to keep
   * track of potential regressions.
   */
  minor: number;
}

export enum PathfinderVersion_ArtifactMajorVersion {
  UNKNOWN = 0,
  /** VERSION_1 - Version 1 of pathfinder artifacts used in Asimov and Battin releases */
  VERSION_1 = 1,
  /** VERSION_2 - Version 2 of pathfinder artifacts used in Cayley */
  VERSION_2 = 2,
  UNRECOGNIZED = -1,
}

export function pathfinderVersion_ArtifactMajorVersionFromJSON(object: any): PathfinderVersion_ArtifactMajorVersion {
  switch (object) {
    case 0:
    case "UNKNOWN":
      return PathfinderVersion_ArtifactMajorVersion.UNKNOWN;
    case 1:
    case "VERSION_1":
      return PathfinderVersion_ArtifactMajorVersion.VERSION_1;
    case 2:
    case "VERSION_2":
      return PathfinderVersion_ArtifactMajorVersion.VERSION_2;
    case -1:
    case "UNRECOGNIZED":
    default:
      return PathfinderVersion_ArtifactMajorVersion.UNRECOGNIZED;
  }
}

export function pathfinderVersion_ArtifactMajorVersionToJSON(object: PathfinderVersion_ArtifactMajorVersion): string {
  switch (object) {
    case PathfinderVersion_ArtifactMajorVersion.UNKNOWN:
      return "UNKNOWN";
    case PathfinderVersion_ArtifactMajorVersion.VERSION_1:
      return "VERSION_1";
    case PathfinderVersion_ArtifactMajorVersion.VERSION_2:
      return "VERSION_2";
    case PathfinderVersion_ArtifactMajorVersion.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/** Settings used to create pathfinder artifacts (excluding input files) */
export interface PathfinderArtifactSettings {
  targetExtentWgs84Wkt: string;
  organizationUuid: string;
  dstResolutionWgs84: number;
  shieldAvoidRadius: number;
  floorAgl: number;
  obstacleRadius: number;
  slicesHeightStep: number;
  slicesHeightMin: number;
  slicesHeightMax: number;
  separateGeospatialOrgFiles: boolean;
  slicesVectorGeometryTolerance: number;
  fullDtedTiles: boolean;
}

/** Defines metadata for pathfinder artifacts in a backwards compatible way */
export interface PathfinderArtifactMetadata {
  /** The file ID of these pathfinder artifacts */
  organizationFileUuid: string;
  /** Major and minor version */
  version:
    | PathfinderVersion
    | undefined;
  /** Pathfinder workflow settings used to create these artifacts */
  settings: PathfinderArtifactSettings | undefined;
}

function createBasePathfinderVersion(): PathfinderVersion {
  return { major: 0, minor: 0 };
}

export const PathfinderVersion = {
  encode(message: PathfinderVersion, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.major !== 0) {
      writer.uint32(16).int32(message.major);
    }
    if (message.minor !== 0) {
      writer.uint32(24).int32(message.minor);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): PathfinderVersion {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePathfinderVersion();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 2:
          if (tag !== 16) {
            break;
          }

          message.major = reader.int32() as any;
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.minor = reader.int32();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): PathfinderVersion {
    return {
      major: isSet(object.major) ? pathfinderVersion_ArtifactMajorVersionFromJSON(object.major) : 0,
      minor: isSet(object.minor) ? globalThis.Number(object.minor) : 0,
    };
  },

  toJSON(message: PathfinderVersion): unknown {
    const obj: any = {};
    if (message.major !== 0) {
      obj.major = pathfinderVersion_ArtifactMajorVersionToJSON(message.major);
    }
    if (message.minor !== 0) {
      obj.minor = Math.round(message.minor);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<PathfinderVersion>, I>>(base?: I): PathfinderVersion {
    return PathfinderVersion.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<PathfinderVersion>, I>>(object: I): PathfinderVersion {
    const message = createBasePathfinderVersion();
    message.major = object.major ?? 0;
    message.minor = object.minor ?? 0;
    return message;
  },
};

function createBasePathfinderArtifactSettings(): PathfinderArtifactSettings {
  return {
    targetExtentWgs84Wkt: "",
    organizationUuid: "",
    dstResolutionWgs84: 0,
    shieldAvoidRadius: 0,
    floorAgl: 0,
    obstacleRadius: 0,
    slicesHeightStep: 0,
    slicesHeightMin: 0,
    slicesHeightMax: 0,
    separateGeospatialOrgFiles: false,
    slicesVectorGeometryTolerance: 0,
    fullDtedTiles: false,
  };
}

export const PathfinderArtifactSettings = {
  encode(message: PathfinderArtifactSettings, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.targetExtentWgs84Wkt !== "") {
      writer.uint32(10).string(message.targetExtentWgs84Wkt);
    }
    if (message.organizationUuid !== "") {
      writer.uint32(18).string(message.organizationUuid);
    }
    if (message.dstResolutionWgs84 !== 0) {
      writer.uint32(29).float(message.dstResolutionWgs84);
    }
    if (message.shieldAvoidRadius !== 0) {
      writer.uint32(37).float(message.shieldAvoidRadius);
    }
    if (message.floorAgl !== 0) {
      writer.uint32(45).float(message.floorAgl);
    }
    if (message.obstacleRadius !== 0) {
      writer.uint32(53).float(message.obstacleRadius);
    }
    if (message.slicesHeightStep !== 0) {
      writer.uint32(61).float(message.slicesHeightStep);
    }
    if (message.slicesHeightMin !== 0) {
      writer.uint32(69).float(message.slicesHeightMin);
    }
    if (message.slicesHeightMax !== 0) {
      writer.uint32(77).float(message.slicesHeightMax);
    }
    if (message.separateGeospatialOrgFiles === true) {
      writer.uint32(80).bool(message.separateGeospatialOrgFiles);
    }
    if (message.slicesVectorGeometryTolerance !== 0) {
      writer.uint32(93).float(message.slicesVectorGeometryTolerance);
    }
    if (message.fullDtedTiles === true) {
      writer.uint32(96).bool(message.fullDtedTiles);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): PathfinderArtifactSettings {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePathfinderArtifactSettings();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.targetExtentWgs84Wkt = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.organizationUuid = reader.string();
          continue;
        case 3:
          if (tag !== 29) {
            break;
          }

          message.dstResolutionWgs84 = reader.float();
          continue;
        case 4:
          if (tag !== 37) {
            break;
          }

          message.shieldAvoidRadius = reader.float();
          continue;
        case 5:
          if (tag !== 45) {
            break;
          }

          message.floorAgl = reader.float();
          continue;
        case 6:
          if (tag !== 53) {
            break;
          }

          message.obstacleRadius = reader.float();
          continue;
        case 7:
          if (tag !== 61) {
            break;
          }

          message.slicesHeightStep = reader.float();
          continue;
        case 8:
          if (tag !== 69) {
            break;
          }

          message.slicesHeightMin = reader.float();
          continue;
        case 9:
          if (tag !== 77) {
            break;
          }

          message.slicesHeightMax = reader.float();
          continue;
        case 10:
          if (tag !== 80) {
            break;
          }

          message.separateGeospatialOrgFiles = reader.bool();
          continue;
        case 11:
          if (tag !== 93) {
            break;
          }

          message.slicesVectorGeometryTolerance = reader.float();
          continue;
        case 12:
          if (tag !== 96) {
            break;
          }

          message.fullDtedTiles = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): PathfinderArtifactSettings {
    return {
      targetExtentWgs84Wkt: isSet(object.targetExtentWgs84Wkt) ? globalThis.String(object.targetExtentWgs84Wkt) : "",
      organizationUuid: isSet(object.organizationUuid) ? globalThis.String(object.organizationUuid) : "",
      dstResolutionWgs84: isSet(object.dstResolutionWgs84) ? globalThis.Number(object.dstResolutionWgs84) : 0,
      shieldAvoidRadius: isSet(object.shieldAvoidRadius) ? globalThis.Number(object.shieldAvoidRadius) : 0,
      floorAgl: isSet(object.floorAgl) ? globalThis.Number(object.floorAgl) : 0,
      obstacleRadius: isSet(object.obstacleRadius) ? globalThis.Number(object.obstacleRadius) : 0,
      slicesHeightStep: isSet(object.slicesHeightStep) ? globalThis.Number(object.slicesHeightStep) : 0,
      slicesHeightMin: isSet(object.slicesHeightMin) ? globalThis.Number(object.slicesHeightMin) : 0,
      slicesHeightMax: isSet(object.slicesHeightMax) ? globalThis.Number(object.slicesHeightMax) : 0,
      separateGeospatialOrgFiles: isSet(object.separateGeospatialOrgFiles)
        ? globalThis.Boolean(object.separateGeospatialOrgFiles)
        : false,
      slicesVectorGeometryTolerance: isSet(object.slicesVectorGeometryTolerance)
        ? globalThis.Number(object.slicesVectorGeometryTolerance)
        : 0,
      fullDtedTiles: isSet(object.fullDtedTiles) ? globalThis.Boolean(object.fullDtedTiles) : false,
    };
  },

  toJSON(message: PathfinderArtifactSettings): unknown {
    const obj: any = {};
    if (message.targetExtentWgs84Wkt !== "") {
      obj.targetExtentWgs84Wkt = message.targetExtentWgs84Wkt;
    }
    if (message.organizationUuid !== "") {
      obj.organizationUuid = message.organizationUuid;
    }
    if (message.dstResolutionWgs84 !== 0) {
      obj.dstResolutionWgs84 = message.dstResolutionWgs84;
    }
    if (message.shieldAvoidRadius !== 0) {
      obj.shieldAvoidRadius = message.shieldAvoidRadius;
    }
    if (message.floorAgl !== 0) {
      obj.floorAgl = message.floorAgl;
    }
    if (message.obstacleRadius !== 0) {
      obj.obstacleRadius = message.obstacleRadius;
    }
    if (message.slicesHeightStep !== 0) {
      obj.slicesHeightStep = message.slicesHeightStep;
    }
    if (message.slicesHeightMin !== 0) {
      obj.slicesHeightMin = message.slicesHeightMin;
    }
    if (message.slicesHeightMax !== 0) {
      obj.slicesHeightMax = message.slicesHeightMax;
    }
    if (message.separateGeospatialOrgFiles === true) {
      obj.separateGeospatialOrgFiles = message.separateGeospatialOrgFiles;
    }
    if (message.slicesVectorGeometryTolerance !== 0) {
      obj.slicesVectorGeometryTolerance = message.slicesVectorGeometryTolerance;
    }
    if (message.fullDtedTiles === true) {
      obj.fullDtedTiles = message.fullDtedTiles;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<PathfinderArtifactSettings>, I>>(base?: I): PathfinderArtifactSettings {
    return PathfinderArtifactSettings.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<PathfinderArtifactSettings>, I>>(object: I): PathfinderArtifactSettings {
    const message = createBasePathfinderArtifactSettings();
    message.targetExtentWgs84Wkt = object.targetExtentWgs84Wkt ?? "";
    message.organizationUuid = object.organizationUuid ?? "";
    message.dstResolutionWgs84 = object.dstResolutionWgs84 ?? 0;
    message.shieldAvoidRadius = object.shieldAvoidRadius ?? 0;
    message.floorAgl = object.floorAgl ?? 0;
    message.obstacleRadius = object.obstacleRadius ?? 0;
    message.slicesHeightStep = object.slicesHeightStep ?? 0;
    message.slicesHeightMin = object.slicesHeightMin ?? 0;
    message.slicesHeightMax = object.slicesHeightMax ?? 0;
    message.separateGeospatialOrgFiles = object.separateGeospatialOrgFiles ?? false;
    message.slicesVectorGeometryTolerance = object.slicesVectorGeometryTolerance ?? 0;
    message.fullDtedTiles = object.fullDtedTiles ?? false;
    return message;
  },
};

function createBasePathfinderArtifactMetadata(): PathfinderArtifactMetadata {
  return { organizationFileUuid: "", version: undefined, settings: undefined };
}

export const PathfinderArtifactMetadata = {
  encode(message: PathfinderArtifactMetadata, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.organizationFileUuid !== "") {
      writer.uint32(10).string(message.organizationFileUuid);
    }
    if (message.version !== undefined) {
      PathfinderVersion.encode(message.version, writer.uint32(18).fork()).ldelim();
    }
    if (message.settings !== undefined) {
      PathfinderArtifactSettings.encode(message.settings, writer.uint32(26).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): PathfinderArtifactMetadata {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBasePathfinderArtifactMetadata();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.organizationFileUuid = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.version = PathfinderVersion.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.settings = PathfinderArtifactSettings.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): PathfinderArtifactMetadata {
    return {
      organizationFileUuid: isSet(object.organizationFileUuid) ? globalThis.String(object.organizationFileUuid) : "",
      version: isSet(object.version) ? PathfinderVersion.fromJSON(object.version) : undefined,
      settings: isSet(object.settings) ? PathfinderArtifactSettings.fromJSON(object.settings) : undefined,
    };
  },

  toJSON(message: PathfinderArtifactMetadata): unknown {
    const obj: any = {};
    if (message.organizationFileUuid !== "") {
      obj.organizationFileUuid = message.organizationFileUuid;
    }
    if (message.version !== undefined) {
      obj.version = PathfinderVersion.toJSON(message.version);
    }
    if (message.settings !== undefined) {
      obj.settings = PathfinderArtifactSettings.toJSON(message.settings);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<PathfinderArtifactMetadata>, I>>(base?: I): PathfinderArtifactMetadata {
    return PathfinderArtifactMetadata.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<PathfinderArtifactMetadata>, I>>(object: I): PathfinderArtifactMetadata {
    const message = createBasePathfinderArtifactMetadata();
    message.organizationFileUuid = object.organizationFileUuid ?? "";
    message.version = (object.version !== undefined && object.version !== null)
      ? PathfinderVersion.fromPartial(object.version)
      : undefined;
    message.settings = (object.settings !== undefined && object.settings !== null)
      ? PathfinderArtifactSettings.fromPartial(object.settings)
      : undefined;
    return message;
  },
};

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
